/**
 * Normalización y validación de `BrickSettings` (contrato §3.2).
 *
 * Centraliza defaults y validaciones para que `bricks.create` y `controller.update`
 * operen sobre un settings canonizado. No lanza por campos opcionales inválidos:
 * aplica defaults seguros y advierte por consola cuando algo se ignora.
 */
import type { BrickSettings } from '../core/api';
import type { FinvaError, FinvaMode, FinvaProduct, FinvaTheme, SubmitData } from '../transport/protocol';
import { FINVA_FLOWS, type FinvaFlow } from '../core/api';

const VALID_MODES: readonly FinvaMode[] = ['embedded', 'modal'];
const VALID_THEMES: readonly FinvaTheme[] = ['light', 'dark'];
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
/** Google Ads ID: `AW-XXXXXXX` o `AW-XXXXXXX/label` (label alfanum + `_-`). */
const GADS_RE = /^AW-\d+(?:\/[A-Za-z0-9_-]+)?$/;
const VALID_FLOWS: Set<FinvaFlow> = new Set(FINVA_FLOWS);

export interface NormalizedCallbacks {
  onReady: () => void;
  onSubmit: (data: SubmitData) => void;
  onApproved: (solicitud: string) => void;
  onError: (error: FinvaError) => void;
}

export interface NormalizedSettings {
  brand?: string;
  mode: FinvaMode;
  prefill?: { nombre?: string; telefono?: string; email?: string };
  product?: FinvaProduct;
  theme: FinvaTheme;
  primaryColor?: string;
  secondaryColor?: string;
  flow?: FinvaFlow;
  availableFlows?: FinvaFlow[];
  analytics?: { metaPixelId?: string; googleAdsId?: string };
  callbacks: NormalizedCallbacks;
}

/**
 * Canoniza los settings entrantes: aplica defaults (`mode: 'embedded'`,
 * `theme: 'light'`), filtra valores inválidos y normaliza `primaryColor` a HEX
 * lower-case. Las callbacks ausentes se rellenan con no-ops para que el resto
 * del SDK pueda invocarlas sin null-checks.
 */
export function normalizeSettings(settings: BrickSettings): NormalizedSettings {
  const init = settings.initialization ?? {};
  const custom = settings.customization ?? {};
  const cb = settings.callbacks ?? {};

  const mode: FinvaMode = VALID_MODES.includes(init.mode as FinvaMode)
    ? (init.mode as FinvaMode)
    : 'embedded';

  const theme: FinvaTheme = VALID_THEMES.includes(custom.theme as FinvaTheme)
    ? (custom.theme as FinvaTheme)
    : 'light';

  let primaryColor: string | undefined;
  if (custom.primaryColor != null && custom.primaryColor !== '') {
    if (HEX_RE.test(custom.primaryColor.trim())) {
      primaryColor = custom.primaryColor.trim().toLowerCase();
    } else {
      warnIgnored('primaryColor', custom.primaryColor, 'no es un HEX válido (#rgb o #rrggbb)');
    }
  }

  let secondaryColor: string | undefined;
  if (custom.secondaryColor != null && custom.secondaryColor !== '') {
    if (HEX_RE.test(custom.secondaryColor.trim())) {
      secondaryColor = custom.secondaryColor.trim().toLowerCase();
    } else {
      warnIgnored('secondaryColor', custom.secondaryColor, 'no es un HEX válido (#rgb o #rrggbb)');
    }
  }

  const brand = init.brand?.trim() || undefined;
  const prefill = init.prefill
    ? {
        nombre: init.prefill.nombre?.trim() || undefined,
        telefono: init.prefill.telefono?.trim() || undefined,
        email: init.prefill.email?.trim() || undefined,
      }
    : undefined;

  const product = normalizeProduct(init.product);
  const analytics = normalizeAnalytics(settings.analytics);
  const flow = normalizeFlow(init.flow);
  const availableFlows = normalizeAvailableFlows(init.availableFlows, flow);

  return {
    brand,
    mode,
    prefill: prefill && (prefill.nombre || prefill.telefono || prefill.email) ? prefill : undefined,
    product,
    theme,
    primaryColor,
    secondaryColor,
    flow,
    availableFlows,
    analytics,
    callbacks: {
      onReady: cb.onReady ?? noop,
      onSubmit: cb.onSubmit ?? noop,
      onApproved: cb.onApproved ?? noop,
      onError: cb.onError ?? noop,
    },
  };
}

/**
 * Mezcla un `Partial<BrickSettings>` sobre un `NormalizedSettings` previo,
 * re-aplicando las mismas reglas de defaults/validación. Devuelve una nueva
 * instancia (no muta la entrada). Usado por `controller.update`.
 */
export function mergeSettings(
  current: NormalizedSettings,
  partial: Partial<BrickSettings>,
): NormalizedSettings {
  const next: BrickSettings = {
    initialization: {
      brand: partial.initialization?.brand ?? current.brand,
      mode: partial.initialization?.mode ?? current.mode,
      prefill: partial.initialization?.prefill ?? current.prefill,
      product: partial.initialization?.product ?? current.product,
      flow: partial.initialization?.flow ?? current.flow,
      availableFlows: partial.initialization?.availableFlows ?? current.availableFlows,
    },
    customization: {
      theme: partial.customization?.theme ?? current.theme,
      primaryColor: partial.customization?.primaryColor ?? current.primaryColor,
      secondaryColor: partial.customization?.secondaryColor ?? current.secondaryColor,
    },
    analytics: partial.analytics ?? current.analytics,
    callbacks: {
      onReady: partial.callbacks?.onReady ?? current.callbacks.onReady,
      onSubmit: partial.callbacks?.onSubmit ?? current.callbacks.onSubmit,
      onApproved: partial.callbacks?.onApproved ?? current.callbacks.onApproved,
      onError: partial.callbacks?.onError ?? current.callbacks.onError,
    },
  };
  return normalizeSettings(next);
}

function noop(): void {
  /* no-op default callback */
}

/**
 * Canoniza `product`: exige `brand`. Limpia strings y normaliza numéricos.
 *
 * `price` es OPCIONAL: cuando el comercio no fija una moto (`finvaMotorcycleId`)
 * el iframe abre el selector de catálogo, que sólo necesita la marca para
 * ofrecer los modelos correctos — el precio sale de ahí. Un `price` presente
 * pero inválido (0, negativo, no numérico) se descarta como si no viniera, en
 * vez de tumbar el `product` entero y dejar al iframe sin marca.
 */
function normalizeProduct(product: BrickSettings['initialization']['product']): FinvaProduct | undefined {
  if (!product) return undefined;
  const brand = product.brand?.trim();
  if (!brand) {
    warnIgnored('product', product, 'requiere brand');
    return undefined;
  }
  const rawPrice = typeof product.price === 'number' ? product.price : Number(product.price);
  const price = Number.isFinite(rawPrice) && rawPrice > 0 ? rawPrice : undefined;
  const months = typeof product.months === 'number' ? product.months : Number(product.months);
  const downPayment =
    typeof product.downPayment === 'number' ? product.downPayment : Number(product.downPayment);
  const year = typeof product.year === 'number' ? product.year : Number(product.year);
  return {
    brand,
    model: product.model?.trim() || undefined,
    slug: product.slug?.trim() || undefined,
    year: Number.isFinite(year) ? year : undefined,
    price,
    finvaMotorcycleId: product.finvaMotorcycleId?.trim() || undefined,
    downPayment: Number.isFinite(downPayment) ? downPayment : undefined,
    months: Number.isFinite(months) ? months : undefined,
  };
}

function warnIgnored(field: string, value: unknown, reason: string): void {
  // eslint-disable-next-line no-console
  console.warn(`Finva: se ignora ${field}=${JSON.stringify(value)} (${reason})`);
}

/**
 * Canoniza los IDs de tracking del merchant. `metaPixelId` se reduce a dígitos
 * (los IDs de Meta son numéricos); inválido si no quedan dígitos. `googleAdsId`
 * debe matchear `AW-\d+(/label)?`. Los IDs inválidos se ignoran con aviso (mismo
 * patrón que `primaryColor`). Devuelve `undefined` si ambos quedan vacíos.
 */
function normalizeAnalytics(
  analytics: BrickSettings['analytics'] | undefined,
): { metaPixelId?: string; googleAdsId?: string } | undefined {
  if (!analytics) return undefined;

  let metaPixelId: string | undefined;
  if (analytics.metaPixelId != null && analytics.metaPixelId !== '') {
    const digits = analytics.metaPixelId.replace(/\D/g, '');
    if (digits) {
      metaPixelId = digits;
    } else {
      warnIgnored('analytics.metaPixelId', analytics.metaPixelId, 'no contiene dígitos');
    }
  }

  let googleAdsId: string | undefined;
  if (analytics.googleAdsId != null && analytics.googleAdsId !== '') {
    const trimmed = analytics.googleAdsId.trim();
    if (GADS_RE.test(trimmed)) {
      googleAdsId = trimmed;
    } else {
      warnIgnored('analytics.googleAdsId', analytics.googleAdsId, 'debe ser AW-XXXXXXX o AW-XXXXXXX/label');
    }
  }

  if (!metaPixelId && !googleAdsId) return undefined;
  return { metaPixelId, googleAdsId };
}

/** Comparación estructural de analytics (para decidir recarga del iframe). */
export function analyticsEqual(
  a?: { metaPixelId?: string; googleAdsId?: string },
  b?: { metaPixelId?: string; googleAdsId?: string },
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.metaPixelId === b.metaPixelId && a.googleAdsId === b.googleAdsId;
}

/**
 * Canoniza `flow`: sólo acepta valores del catálogo `FinvaFlow`; cualquier otra
 * cosa se ignora con aviso (mismo patrón que `primaryColor`). `undefined` → el
 * iframe muestra el selector de flujo.
 */
function normalizeFlow(flow: unknown): FinvaFlow | undefined {
  if (flow == null || flow === '') return undefined;
  if (typeof flow === 'string' && VALID_FLOWS.has(flow as FinvaFlow)) {
    return flow as FinvaFlow;
  }
  warnIgnored('flow', flow, 'debe ser uno de: financed, cash, card');
  return undefined;
}

/**
 * Canoniza `availableFlows`: descarta valores inválidos (con aviso) y dedupe.
 * Si queda vacío y no hay `flow` fijado, devuelve el catálogo completo (los
 * tres flujos) para que el selector siempre tenga algo que mostrar.
 */
function normalizeAvailableFlows(
  flows: unknown,
  flow: FinvaFlow | undefined,
): FinvaFlow[] | undefined {
  if (!Array.isArray(flows)) return undefined;
  const seen = new Set<FinvaFlow>();
  const out: FinvaFlow[] = [];
  for (const f of flows) {
    if (typeof f !== 'string' || !VALID_FLOWS.has(f as FinvaFlow)) {
      warnIgnored('availableFlows', f, 'debe ser uno de: financed, cash, card');
      continue;
    }
    const flow = f as FinvaFlow;
    if (seen.has(flow)) continue; // dedupe silencioso
    seen.add(flow);
    out.push(flow);
  }
  if (out.length === 0 && !flow) return [...FINVA_FLOWS];
  return out.length > 0 ? out : undefined;
}

/** Comparación de `flow` (para decidir recarga del iframe en update). */
export function flowEqual(a?: FinvaFlow, b?: FinvaFlow): boolean {
  return a === b;
}

/** Comparación de `availableFlows` (orden-insensible, sin duplicados). */
export function flowsEqual(a?: readonly FinvaFlow[], b?: readonly FinvaFlow[]): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((f) => bs.has(f));
}